import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCategories } from "../lib/categoriesContext";
import { LANG_LABEL, LEGAL_DISCLAIMER } from "../lib/categories";
import { ArticleCard } from "../components/ArticleCard";
import { Clock, Calendar, Share2, Copy, ExternalLink, Quote } from "lucide-react";

function getYouTubeId(url = "") {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}
function getVimeoId(url = "") {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

function VideoEmbed({ url, caption }) {
  if (!url) return null;
  const yt = getYouTubeId(url);
  const vm = getVimeoId(url);
  let src = null;
  if (yt) src = `https://www.youtube.com/embed/${yt}`;
  else if (vm) src = `https://player.vimeo.com/video/${vm}`;
  if (!src) {
    return (
      <div className="bg-[#F5EFEB] border border-[#E0D8CE] p-6 text-center">
        <a href={url} target="_blank" rel="noopener noreferrer" className="font-mono-label text-[#4A7A3D] underline">Voir la vidéo →</a>
      </div>
    );
  }
  return (
    <figure className="my-2">
      <div className="aspect-video bg-black">
        <iframe src={src} title="Vidéo" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
      </div>
      {caption && <figcaption className="font-mono-label text-[#4B4B48] mt-3">{caption}</figcaption>}
    </figure>
  );
}

function AudioEmbed({ url, caption }) {
  if (!url) return null;
  const isSoundCloud = /soundcloud\.com/.test(url);
  const isSpotify = /open\.spotify\.com/.test(url);
  if (isSoundCloud) {
    return (
      <figure className="my-2">
        <iframe width="100%" height="166" scrolling="no" frameBorder="no"
          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%234A7A3D&inverse=false&auto_play=false&show_user=true`} title="Podcast" />
        {caption && <figcaption className="font-mono-label text-[#4B4B48] mt-3">{caption}</figcaption>}
      </figure>
    );
  }
  if (isSpotify) {
    const embed = url.replace("open.spotify.com/", "open.spotify.com/embed/");
    return (
      <figure className="my-2">
        <iframe src={embed} width="100%" height="232" frameBorder="0" allow="encrypted-media" title="Spotify" />
        {caption && <figcaption className="font-mono-label text-[#4B4B48] mt-3">{caption}</figcaption>}
      </figure>
    );
  }
  return (
    <figure className="my-2">
      <audio controls src={url} className="w-full" />
      {caption && <figcaption className="font-mono-label text-[#4B4B48] mt-3">{caption}</figcaption>}
    </figure>
  );
}

function ShareButtons({ article }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = encodeURIComponent(`${article.title} — RWANA BIZIN LEGA`);
  const u = encodeURIComponent(url);
  return (
    <div className="flex flex-wrap gap-2">
      <a href={`https://wa.me/?text=${text}%20${u}`} target="_blank" rel="noopener noreferrer" className="bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#3E6633] flex items-center gap-2" data-testid="share-whatsapp">
        <Share2 size={14} /> WhatsApp
      </a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${u}`} target="_blank" rel="noopener noreferrer" className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3] flex items-center gap-2" data-testid="share-facebook">
        <Share2 size={14} /> Facebook
      </a>
      <button onClick={() => { navigator.clipboard?.writeText(url); }} className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3] flex items-center gap-2" data-testid="share-copy">
        <Copy size={14} /> Copier
      </button>
    </div>
  );
}

function MediaBlockRender({ block }) {
  if (block.type === "image" && block.url) {
    return (
      <figure className="my-8">
        <img src={block.url} alt={block.caption || ""} className="w-full" />
        {block.caption && <figcaption className="font-mono-label text-[#4B4B48] mt-3">{block.caption}</figcaption>}
      </figure>
    );
  }
  if (block.type === "video" && block.url) return <div className="my-8"><VideoEmbed url={block.url} caption={block.caption} /></div>;
  if (block.type === "audio" && block.url) return <div className="my-8"><AudioEmbed url={block.url} caption={block.caption} /></div>;
  if (block.type === "quote" && block.text) {
    return (
      <blockquote className="my-10 border-l-4 border-[#C4944A] pl-6 italic">
        <Quote size={18} className="text-[#C4944A] mb-2" />
        <p className="font-quote text-2xl text-[#1A1A18] leading-snug">« {block.text} »</p>
        {block.caption && <footer className="font-mono-label text-[#4B4B48] mt-3 not-italic">— {block.caption}</footer>}
      </blockquote>
    );
  }
  return null;
}

export default function Article() {
  const { slug } = useParams();
  const { categories } = useCategories();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-24 font-mono-label">CHARGEMENT…</div>;
  if (notFound || !data) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <h1 className="font-display text-5xl">Article introuvable</h1>
      <Link to="/" className="font-mono-label text-[#4A7A3D] mt-4 inline-block" data-testid="article-not-found-back">← Retour</Link>
    </div>
  );

  const a = data.article;
  const cat = a.category || categories.find((c) => c.slug === a.category_slug);
  const date = a.published_at ? new Date(a.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";

  // Parse paragraphs and merge media blocks at correct positions
  const paragraphs = (a.body || "").split(/\n{2,}/);
  const mediaByPosition = {};
  (a.media_blocks || []).forEach((b) => {
    const pos = Math.max(0, Math.min(b.position || 0, paragraphs.length));
    (mediaByPosition[pos] = mediaByPosition[pos] || []).push(b);
  });

  const renderPara = (p, i) => {
    const html = p.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br/>");
    return <p key={`p-${i}`} dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <article data-testid="article-page">
      <header className="border-b border-[#E0D8CE] relative">
        {cat && <span className="absolute top-0 left-0 w-full h-1.5" style={{ backgroundColor: cat.accent_color || "#4A7A3D" }} />}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10">
          {cat && (
            <Link to={`/rubrique/${cat.slug}`} className="font-mono-label text-[#4A7A3D] inline-block mb-6 hover:underline" data-testid="article-category-link">
              ← {cat.name}
            </Link>
          )}
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.02] text-[#1A1A18]">{a.title}</h1>
          {a.subtitle && <p className="font-quote text-xl md:text-2xl text-[#4B4B48] mt-6 leading-snug">{a.subtitle}</p>}
          <div className="mt-8 pt-6 border-t border-[#E0D8CE] flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#4B4B48]">
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {date}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {a.reading_time || 5} min de lecture</span>
            <span className="font-mono-label">{LANG_LABEL[a.language] || a.language}</span>
            <span className="font-mono-label">par {a.author || "Rédaction RWANA"}</span>
          </div>
        </div>
      </header>

      {/* Featured image (skipped for video/podcast templates which display the embed instead) */}
      {a.featured_image && a.template !== "video" && a.template !== "podcast" && (
        <div className="border-b border-[#E0D8CE] bg-[#F5EFEB]">
          <div className={`mx-auto ${a.template === "infographic" ? "max-w-7xl" : "max-w-5xl"}`}>
            <img src={a.featured_image} alt={a.title} className={`w-full object-cover ${a.template === "infographic" ? "" : "aspect-[16/9]"}`} />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Template-specific top blocks */}
        {a.template === "video" && (a.video_url || a.featured_image) && (
          <div className="mb-10">
            <VideoEmbed url={a.video_url} caption={a.video_caption} />
          </div>
        )}
        {a.template === "podcast" && (a.audio_url || a.embed_html) && (
          <div className="mb-10">
            {a.embed_html ? (
              <div dangerouslySetInnerHTML={{ __html: a.embed_html }} />
            ) : (
              <AudioEmbed url={a.audio_url} caption={a.audio_caption} />
            )}
          </div>
        )}

        {a.template === "myth_buster" && (a.myth || a.reality) && (
          <div className="grid md:grid-cols-2 mb-12 border-2 border-[#1A1A18]">
            <div className="bg-[#FAF8F3] p-6 border-r-2 border-b-2 md:border-b-0 border-[#1A1A18]">
              <div className="font-mono-label text-[#C4944A] mb-3">Mythe</div>
              <p className="font-quote text-lg leading-snug">« {a.myth} »</p>
            </div>
            <div className="bg-[#1A1A18] text-[#FAF8F3] p-6">
              <div className="font-mono-label text-[#8FAF82] mb-3">Réalité</div>
              <p className="leading-relaxed">{a.reality}</p>
            </div>
          </div>
        )}

        {a.template === "data" && a.stat_value && (
          <div className="bg-[#1A1A18] text-[#FAF8F3] p-8 md:p-12 mb-10">
            <div className="font-display text-7xl md:text-9xl text-[#C4944A] leading-none">{a.stat_value}</div>
            <div className="font-display text-xl md:text-2xl mt-4">{a.stat_label}</div>
          </div>
        )}

        {a.template === "case_study" && a.country && (
          <div className="font-mono-label inline-block bg-[#8FAF82]/30 text-[#4A7A3D] px-3 py-1.5 mb-8" data-testid="article-case-country">
            ÉTUDE DE CAS · {a.country}
          </div>
        )}

        {a.template === "qa" && a.qa_pairs?.length > 0 && (
          <div className="mb-12 space-y-8" data-testid="article-qa">
            {a.qa_pairs.map((qa, i) => (
              <div key={i} className="border-b border-[#E0D8CE] pb-8 last:border-0">
                <div className="flex gap-4">
                  <span className="font-display text-3xl text-[#C4944A] flex-shrink-0">Q.</span>
                  <p className="font-display text-xl md:text-2xl leading-snug">{qa.question}</p>
                </div>
                <div className="flex gap-4 mt-4">
                  <span className="font-display text-3xl text-[#4A7A3D] flex-shrink-0">R.</span>
                  <p className="text-base md:text-lg leading-[1.7]">{qa.answer}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {a.template === "gallery" && a.gallery_images?.length > 0 && (
          <div className="mb-10 grid grid-cols-2 gap-2 md:gap-3" data-testid="article-gallery">
            {a.gallery_images.map((img, i) => (
              <figure key={i} className={`${i === 0 && a.gallery_images.length > 3 ? "col-span-2" : ""}`}>
                <img src={img.url} alt={img.caption || ""} className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
                {img.caption && <figcaption className="font-mono-label text-[#4B4B48] mt-2">{img.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}

        {/* Body interleaved with media blocks */}
        <div className="prose-rwana">
          {mediaByPosition[0]?.map((b, i) => <MediaBlockRender key={`m0-${i}`} block={b} />)}
          {paragraphs.map((p, i) => (
            <span key={`pw-${i}`}>
              {renderPara(p, i)}
              {mediaByPosition[i + 1]?.map((b, j) => <MediaBlockRender key={`m-${i}-${j}`} block={b} />)}
            </span>
          ))}
        </div>

        {/* Key facts */}
        {a.key_facts?.length > 0 && (
          <aside className="mt-12 border-l-4 border-[#4A7A3D] bg-[#F5EFEB] p-6">
            <div className="font-mono-label text-[#4A7A3D] mb-3">À RETENIR</div>
            <ul className="space-y-2">
              {a.key_facts.map((f, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-display text-[#C4944A]">{i + 1}.</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Tags */}
        {a.tags?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-[#E0D8CE]">
            <div className="font-mono-label text-[#4B4B48] mb-3">Mots-clés</div>
            <div className="flex flex-wrap gap-2">
              {a.tags.map((t) => (
                <Link key={t} to={`/rubrique/${a.category_slug}?tag=${encodeURIComponent(t)}`} className="font-mono-label bg-[#FAF8F3] border border-[#E0D8CE] px-3 py-1.5 hover:border-[#1A1A18]">
                  #{t}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-10 p-6 bg-[#1A1A18] text-[#FAF8F3]" data-testid="article-disclaimer">
          <div className="font-mono-label text-[#C4944A] mb-2">AVERTISSEMENT LÉGAL</div>
          <p className="text-sm leading-relaxed text-[#FAF8F3]/85">{LEGAL_DISCLAIMER}</p>
        </div>

        {/* Sources */}
        {a.sources?.length > 0 && (
          <div className="mt-10">
            <div className="font-mono-label text-[#4A7A3D] mb-3">Sources</div>
            <ul className="space-y-2">
              {a.sources.map((s, i) => (
                <li key={i} className="text-sm">
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#1A1A18] underline decoration-[#C4944A] decoration-2 underline-offset-4 hover:text-[#4A7A3D] inline-flex items-center gap-1.5" data-testid={`article-source-${i}`}>
                    {s.label} <ExternalLink size={12} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-12 pt-8 border-t-2 border-[#1A1A18]">
          <div className="font-mono-label text-[#4B4B48] mb-3">Partager cet article</div>
          <ShareButtons article={a} />
        </div>
      </div>

      {data.related?.length > 0 && (
        <section className="border-t-2 border-[#1A1A18] bg-[#F5EFEB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="font-mono-label text-[#4A7A3D] mb-2">Continuer la lecture</div>
            <h2 className="font-display text-3xl md:text-4xl mb-10">Articles liés</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {data.related.map((r) => <ArticleCard key={r.slug} article={r} />)}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
